// Unified Review Workflow Integration — reviews-due predicate and ordering.
//
// Pure, side-effect-free selectors (no network, no DOM) that decide which
// review cycles are *due* for a unified pass and the order in which the
// Reviews_Due_Queue presents them. Keeping this logic pure is what makes it
// property-testable across its full input space (design.md "Pure selectors").
//
// References: design.md "Pure selectors (client/src/lib/reviewWorkflow) →
// due.ts"; requirements 2.1, 2.2.

import type { CycleStatus, ReviewCycleRecord } from "./types";

/**
 * One item in the Dashboard Reviews_Due_Queue
 * (`GET /api/review-cycles/reviews-due`). A due cycle always carries a
 * `dueDate` and a `customerName`, and its status can never be a terminal
 * `completed`/`cancelled` value (those cycles are excluded by `isCycleDue`).
 *
 * Mirrors design.md "Data Models → DueReviewCycle".
 */
export interface DueReviewCycle {
  id: number;
  customerId: number;
  customerName: string;
  /** Always present for a due cycle. */
  dueDate: string;
  status: Exclude<CycleStatus, "completed" | "cancelled">;
}

/**
 * Whether a review cycle qualifies as *due* for a unified pass at the reference
 * instant `now` (Req 2.1).
 *
 * A cycle is due if and only if:
 * - its `status` is neither `"completed"` nor `"cancelled"`, AND
 * - it has a `dueDate` that is on or before `now`.
 *
 * A cycle with a `null` `dueDate` is never due — there is no scheduled instant
 * on or before which it could fall.
 */
export function isCycleDue(cycle: ReviewCycleRecord, now: Date): boolean {
  if (cycle.status === "completed" || cycle.status === "cancelled") {
    return false;
  }

  if (cycle.dueDate === null) {
    return false;
  }

  const dueTime = new Date(cycle.dueDate).getTime();
  if (Number.isNaN(dueTime)) {
    return false;
  }

  return dueTime <= now.getTime();
}

/**
 * Orders due cycles by `dueDate` ascending, so the most overdue cycle (the
 * earliest due date) appears first (Req 2.2).
 *
 * Returns a new array; the input is not mutated. Cycles whose `dueDate` cannot
 * be parsed sort after parseable dates, preserving a deterministic order.
 */
export function sortDueCycles(cycles: DueReviewCycle[]): DueReviewCycle[] {
  return [...cycles].sort((a, b) => {
    const aTime = new Date(a.dueDate).getTime();
    const bTime = new Date(b.dueDate).getTime();
    const aValid = !Number.isNaN(aTime);
    const bValid = !Number.isNaN(bTime);

    if (!aValid && !bValid) return 0;
    if (!aValid) return 1;
    if (!bValid) return -1;

    return aTime - bTime;
  });
}
